import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail, fileToBase64 } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Paperclip, Send, X, Edit2, Check, XCircle, Lock } from "lucide-react";

export default function CommentsThread({ shipmentId, comments, onRefresh, canCollaborate = true, currentUserId, isBuyer = false }) {
  const [text, setText] = useState("");
  const [atts, setAtts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const fileRef = useRef(null);

  const addAttachment = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setAtts((a) => [...a, { filename: file.name, content_base64: b64 }]);
  };

  const send = async () => {
    if (!text.trim() && atts.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/shipments/${shipmentId}/comments`, { message: text, attachments: atts });
      setText("");
      setAtts([]);
      await onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.message);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };
  const saveEdit = async (c) => {
    if (!editText.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/shipments/${shipmentId}/comments/${c.id}`, {
        message: editText,
        attachments: c.attachments || [],
      });
      setEditingId(null);
      setEditText("");
      await onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle className="w-4 h-4 text-[#6B0F1A]" />
        <h3 className="font-display font-bold text-[#1F1F1F]">Discussion ({comments?.length || 0})</h3>
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {(!comments || comments.length === 0) && (
          <div className="text-sm text-stone-400 text-center py-6">No messages yet</div>
        )}
        {comments?.map((c) => {
          const isOwn = c.author_id === currentUserId;
          const canEdit = canCollaborate && isOwn;
          const isEditing = editingId === c.id;
          return (
            <div key={c.id} className="bg-stone-50 rounded-xl p-3" data-testid={`comment-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-[#6B0F1A] flex items-center gap-1.5">
                  {c.author_name}
                  {c.author_role === "buyer" && (
                    <span className="text-[9px] uppercase tracking-wider bg-[#F4E8E9] text-[#6B0F1A] px-1.5 py-0.5 rounded-full">Buyer</span>
                  )}
                </div>
                <div className="text-[10px] text-stone-400">{new Date(c.created_at).toLocaleString()}{c.updated_at ? " · edited" : ""}</div>
              </div>
              {isEditing ? (
                <div>
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={2}
                    className="w-full bg-white border border-stone-300 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#A8323F]"
                    data-testid={`comment-edit-input-${c.id}`}
                  />
                  <div className="flex justify-end gap-1.5 mt-1.5">
                    <button onClick={cancelEdit} className="text-stone-500 hover:text-stone-700 text-xs inline-flex items-center gap-1" data-testid={`comment-edit-cancel-${c.id}`}><XCircle className="w-3.5 h-3.5" /> Cancel</button>
                    <button onClick={() => saveEdit(c)} disabled={busy} className="bg-[#6B0F1A] text-white text-xs font-semibold rounded-lg px-2.5 py-1 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`comment-edit-save-${c.id}`}><Check className="w-3.5 h-3.5" /> Save</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-sm text-[#1F1F1F] whitespace-pre-wrap">{c.message}</div>
                  {c.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {c.attachments.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-white border border-stone-200 rounded-full px-2 py-0.5 text-[11px] text-stone-600">
                          <Paperclip className="w-3 h-3" /> {a.filename}
                        </span>
                      ))}
                    </div>
                  )}
                  {canEdit && (
                    <div className="flex justify-end mt-1.5">
                      <button onClick={() => startEdit(c)} className="text-stone-400 hover:text-[#6B0F1A] text-[11px] inline-flex items-center gap-1" data-testid={`comment-edit-${c.id}`}>
                        <Edit2 className="w-3 h-3" /> Edit
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {canCollaborate ? (
        <div className="mt-3 border-t border-stone-100 pt-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder={isBuyer ? "Share an update or upload a delivery document..." : "Add a note..."}
            className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A8323F]"
            data-testid="comment-input"
          />
          {atts.length > 0 && (
            <div className="flex flex-wrap gap-1 my-2">
              {atts.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-[#F4E8E9] text-[#6B0F1A] rounded-full px-2 py-0.5 text-xs">
                  {a.filename}
                  <button onClick={() => setAtts((x) => x.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <button onClick={() => fileRef.current?.click()} className="text-stone-500 hover:text-[#6B0F1A] text-xs font-semibold inline-flex items-center gap-1" data-testid="comment-attach">
              <Paperclip className="w-4 h-4" /> Attach
            </button>
            <button
              onClick={send}
              disabled={busy || (!text.trim() && atts.length === 0)}
              className="inline-flex items-center gap-1.5 bg-[#6B0F1A] text-white text-sm font-semibold rounded-xl px-4 py-2 hover:bg-[#56121C] disabled:opacity-50"
              data-testid="comment-send"
            >
              <Send className="w-3.5 h-3.5" /> Send
            </button>
          </div>
          <input ref={fileRef} type="file" hidden onChange={(e) => addAttachment(e.target.files?.[0])} />
        </div>
      ) : (
        <div className="mt-3 border-t border-stone-100 pt-3 text-xs text-stone-500 inline-flex items-center gap-1.5" data-testid="comment-locked">
          <Lock className="w-3.5 h-3.5" /> Buyer comments unlock at the Delivery stage.
        </div>
      )}
    </div>
  );
}
